/**
 * Case Study Edit Dialog
 * Dialog for editing case study details
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Grid
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';

interface CaseStudyEditDialogProps {
  open: boolean;
  caseStudy: any;
  onClose: () => void;
  onSave: (caseStudyData: any) => Promise<void>;
}

export const CaseStudyEditDialog: React.FC<CaseStudyEditDialogProps> = ({
  open,
  caseStudy,
  onClose,
  onSave
}) => {
  const [formData, setFormData] = useState({
    organization: '',
    focus: '',
    insights: '',
    color: '#003366'
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (caseStudy) {
      setFormData({
        organization: caseStudy.organization || '',
        focus: caseStudy.focus || '',
        insights: caseStudy.insights || '',
        color: caseStudy.color || '#003366'
      });
    }
  }, [caseStudy]);

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    console.log('CaseStudyEditDialog: handleSave called');
    console.log('CaseStudyEditDialog: formData =', formData);
    try {
      setSaving(true);
      await onSave(formData);
      onClose();
    } catch (error) {
      console.error('Error saving case study:', error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ backgroundColor: 'primary.main', color: 'white' }}>
        Edit Case Study: {caseStudy?.organization}
      </DialogTitle>
      <DialogContent sx={{ mt: 2 }}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Organization"
              value={formData.organization}
              onChange={(e) => handleChange('organization', e.target.value)}
              autoFocus
              aria-label="Organization name"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Focus Area"
              value={formData.focus}
              onChange={(e) => handleChange('focus', e.target.value)}
              aria-label="Focus area"
              placeholder="e.g., Data Governance & Growth, AI in Healthcare"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Key Insights"
              value={formData.insights}
              onChange={(e) => handleChange('insights', e.target.value)}
              aria-label="Key insights"
              placeholder="Describe the key insights and learnings from this case study"
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} startIcon={<CancelIcon />}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          startIcon={<SaveIcon />}
          disabled={saving}
          sx={{ backgroundColor: 'primary.main' }}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
